import type { Store } from '@reduxjs/toolkit';
import type { AppState } from '@src/providers/AppStateProvider/types';
import {
  CustomerDealSummary,
  QuoteStatusEnum,
  isEntityRFQ,
  isOrderComplete,
  request,
  wsScanToMap,
  type CareOrder,
  type CustomerQuote,
  type ICustomerOrderSummary,
  type Order,
  type Quote,
  type SubscriptionResponse,
} from '@talos/kyoko';
import { combineLatest, delay, from, map, mergeMap, of, type Observable, type Subscription } from 'rxjs';
import { catchError, scan, startWith } from 'rxjs/operators';
import { setOrderCardsData, setRFQCardsData } from './ActiveOrderCardsSlice';

export class CardsService {
  private sessionStart = new Date();
  private subscriptions: Subscription[] = [];

  constructor(
    private store: Store<AppState>,
    private recentOrdersSub: Observable<Order[]>,
    private filledQuotesSub: Observable<SubscriptionResponse<Quote>>,
    private customerFilledQuotesSub: Observable<SubscriptionResponse<CustomerQuote>>,
    private careOrdersObs: Observable<Map<string, CareOrder>>,
    private workingOrdersObs: Observable<Map<string, CareOrder>>,
    private orgApiEndpoint: string,
    private username: string,
    private customerSummaryDelayMs: number
  ) {
    this.observeQuotes();
    this.observeOrders();
  }

  private observeOrders(): void {
    const ownOrderDataSubscription = this.recentOrdersSub
      .pipe(
        map(orders => {
          return (
            orders
              .filter(order => {
                // Guard against Principal API and Customer API getting out of sync; we only show a customer card if
                // Customer API also agrees with it
                if (order.customerParentOrderID && !order.hasBeenEnrichedWithCustomerOrder) {
                  return false;
                }

                // Don't show ddh hedge (child) orders
                if (order.isDDHHedgeOrder) {
                  return false;
                }

                return true;
              })
              // Filter out RFQs they are populated from Quotes subscription
              .filter(order => !isEntityRFQ(order))
              // Remove any filled orders that were DFD before the session started.
              // Orders which are complete but have been submitted or have been filled after the session start should
              // still be shown.
              .filter(
                order =>
                  !isOrderComplete(order.OrdStatus) ||
                  (order.SubmitTime !== undefined && new Date(order.SubmitTime) > this.sessionStart) ||
                  (order.TransactTime !== undefined && new Date(order.TransactTime) > this.sessionStart)
              )
              .map(order => {
                return {
                  order,
                  submitTime: order.SubmitTime!,
                };
              })
          );
        })
      )
      .subscribe(orderCards => {
        const newMap = orderCards.reduce((acc, value) => {
          return acc.set(value.order.OrderID, value);
        }, new Map());

        this.store.dispatch(setOrderCardsData(newMap));
      });

    this.subscriptions.push(ownOrderDataSubscription);
  }

  private observeQuotes(): void {
    const filledQuotes = this.filledQuotesSub.pipe(
      wsScanToMap({ getUniqueKey: d => d.RFQID, newMapEachUpdate: false }),
      map(m => [...m.values()])
    );

    this.subscriptions.push(
      combineLatest([filledQuotes, this.getCustomerQuotesLookup(), this.careOrdersObs, this.workingOrdersObs])
        .pipe(
          map(([quotes, customerQuotes, careOrders, workingOrders]) => {
            return quotes
              .filter(quote => {
                if (quote.ParentRFQID) {
                  // If the quote has a ParentRFQID, its either a CareOrder, WorkingOrder, or customer order.
                  if (careOrders.has(quote.ParentRFQID) || workingOrders.has(quote.ParentRFQID)) {
                    return true;
                  }
                  // Guard against Principal API and Customer API getting out of sync; we only show a customer card if Customer API also agrees with it
                  return customerQuotes.has(quote.ParentRFQID);
                }
                return true;
              })
              .map(quote => {
                return {
                  quote,
                  submitTime: quote.SubmitTime!,
                  customerQuote: quote.ParentRFQID ? customerQuotes.get(quote.ParentRFQID) : undefined,
                };
              });
          })
        )
        .subscribe(rfqCards => {
          const newMap = rfqCards.reduce((acc, value) => {
            return acc.set(value.quote.RFQID, value);
          }, new Map());
          this.store.dispatch(setRFQCardsData(newMap));
        })
    );
  }

  private getCustomerQuotesLookup(): Observable<Map<string, CustomerQuote>> {
    return this.customerFilledQuotesSub.pipe(
      mergeMap(response => {
        return from(response.data || []);
      }),
      // there is a time gap between quote transitioning to Filled and the summary being generated
      // we want to be able to have the right delay value here
      delay(this.customerSummaryDelayMs),
      mergeMap(customerQuote => {
        if (
          customerQuote.QuoteStatus === QuoteStatusEnum.Filled &&
          new Date(customerQuote.SubmitTime) > this.sessionStart &&
          this.username === customerQuote.User
        ) {
          return from(
            request<SubscriptionResponse<ICustomerOrderSummary>>(
              'GET',
              `${this.orgApiEndpoint}/customer/quotes/${customerQuote.RFQID}/summary-v2`
            )
          ).pipe(
            map(response => ({
              ...customerQuote,
              summary: new CustomerDealSummary(response?.data?.[0]),
            })),
            catchError(() => of(customerQuote))
          );
        }
        return of(customerQuote);
      }),
      scan((map, customerQuote) => {
        map.set(customerQuote.RFQID, customerQuote);
        return map;
      }, new Map()),
      startWith(new Map())
    );
  }

  public destroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
